/**
 * SEO Head Component
 * Dynamically updates canonical URL, meta tags based on current route
 * Ensures all pages point to zafco.in (apex) as canonical domain
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const CANONICAL_DOMAIN = 'https://zafco.in';

/**
 * Production hostnames that MUST remain crawlable / indexable.
 * Every other hostname (preview, staging, *.emergent.host,
 * *.preview.emergentagent.com, localhost, dev URLs, etc.) gets a
 * sitewide `noindex, nofollow` robots directive injected by
 * `useEnvironmentRobots()` at app boot.
 *
 * Update this list ONLY when adding a new live production domain.
 */
const PRODUCTION_HOSTNAMES = new Set([
  'zafco.in',
  'www.zafco.in',
]);

export const isProductionHost = (hostname) => {
  if (!hostname) return false;
  return PRODUCTION_HOSTNAMES.has(hostname.toLowerCase());
};

/**
 * Sitewide environment-aware robots directive.
 *
 * On NON-production hostnames (preview / staging / *.emergent.host /
 * *.preview.emergentagent.com / localhost / any temporary deployment),
 * inject `<meta name="robots" content="noindex, nofollow">` into <head>
 * so search engines NEVER index those builds.
 *
 * On production (`zafco.in`, `www.zafco.in`) this hook is a no-op —
 * the site stays fully crawlable and indexable.
 *
 * The injected tag carries `data-injected-by="env-noindex"` so the
 * `useRobotsReset` safety-net sweeper preserves it (the sweeper only
 * strips unmarked stray tags). Admin pages using `useNoIndex` continue
 * to work — they layer their own marker on top.
 *
 * Mount ONCE at the very top of <App>, before any router renders.
 */
export const useEnvironmentRobots = () => {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.location) return undefined;
    // NEVER bake the env-noindex tag into prerendered HTML: react-snap crawls
    // on localhost during the production build, and its snapshots are served
    // verbatim on zafco.in. react-snap identifies itself via userAgent.
    if ((navigator.userAgent || '').includes('ReactSnap')) return undefined;
    const host = window.location.hostname || '';
    if (isProductionHost(host)) {
      // Production: defensively remove any lingering env-noindex tag (in case
      // the bundle was previously served from a preview host and cached).
      document
        .querySelectorAll('meta[name="robots"][data-injected-by="env-noindex"]')
        .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      return undefined;
    }

    // Non-production host → inject (or reuse) the env noindex tag.
    let tag = document.querySelector('meta[name="robots"][data-injected-by="env-noindex"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'robots');
      tag.setAttribute('data-injected-by', 'env-noindex');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', 'noindex, nofollow');

    return undefined;
  }, []);
};

export const useCanonicalURL = () => {
  const location = useLocation();

  useEffect(() => {
    // Page-owned canonicals (react-helmet / react-helmet-async on pages that
    // intentionally canonicalize to an alias, e.g. calculators/blog posts)
    // take precedence. We mark the tag WE manage with `data-zafco-canonical`;
    // any unmarked canonical is page-owned. Exactly ONE canonical must exist.
    const MARKER = 'data-zafco-canonical';
    function dedupeAgainstPageOwned() {
      const pageOwned = Array.from(
        document.querySelectorAll(`link[rel="canonical"]:not([${MARKER}])`)
      );
      if (pageOwned.length) {
        document.querySelectorAll(`link[rel="canonical"][${MARKER}]`).forEach((el) => el.remove());
        // If multiple page-owned tags exist (stale prerender snapshot + live
        // helmet), keep only the newest (last in DOM order).
        pageOwned.slice(0, -1).forEach((el) => el.remove());
        return true;
      }
      return false;
    }
    function ensureOwnCanonical() {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute(MARKER, '1');
      link.setAttribute('href', `${CANONICAL_DOMAIN}${location.pathname}`);
      return link;
    }

    // Lazy-loaded pages (React.lazy calculators) mount their <Helmet> AFTER
    // this effect runs. Watch <head> so we always converge to ONE canonical:
    // page-owned wins; if all canonicals vanish, restore the default one.
    const observer = new MutationObserver(() => {
      if (dedupeAgainstPageOwned()) return;
      if (!document.querySelector('link[rel="canonical"]')) ensureOwnCanonical();
    });
    observer.observe(document.head, { childList: true });

    if (!dedupeAgainstPageOwned()) {
      // Remove stray extra canonicals, then claim/create the single tag.
      const extras = document.querySelectorAll('link[rel="canonical"]');
      extras.forEach((el, i) => { if (i > 0) el.remove(); });
      const canonicalLink = ensureOwnCanonical();

      // Update og:url meta tag
      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) {
        ogUrl.setAttribute('content', canonicalLink.getAttribute('href'));
      }
    }

    return () => {
      observer.disconnect();
      // Canonical element itself persists intentionally across routes.
    };
  }, [location.pathname]);
};

/**
 * SEO component for admin pages - adds noindex.
 *
 * IMPORTANT: On cleanup we REMOVE the meta tag entirely (instead of toggling
 * to "index, follow"). A residual robots tag in the DOM after SPA navigation
 * to a public page caused Google Search Console to flag public URLs as
 * "Excluded by 'noindex' tag" (June 2026 GSC alert). Removing the tag
 * restores the default crawl behavior (index, follow) without leaving any
 * directive that prerender services / Googlebot could misread.
 */
export const useNoIndex = ({ follow = false } = {}) => {
  useEffect(() => {
    // If a sitewide env-noindex tag is already present (preview/staging),
    // do NOTHING — the env tag already enforces `noindex, nofollow` and
    // owns its own lifecycle. Touching it would risk stripping the marker
    // and letting `useRobotsReset` remove the tag on the next route change.
    const envTag = document.querySelector(
      'meta[name="robots"][data-injected-by="env-noindex"]'
    );
    if (envTag) {
      return undefined;
    }

    // Track whether WE created the tag so cleanup only removes our own injection
    let robotsMeta = document.querySelector('meta[name="robots"]');
    const createdByUs = !robotsMeta;

    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }

    const previousContent = createdByUs ? null : robotsMeta.getAttribute('content');
    // Default: noindex,nofollow (Admin/private pages).
    // Pass { follow: true } for thin utility templates that should NOT be indexed
    // but SHOULD pass crawl through their internal links — preserves link equity.
    robotsMeta.setAttribute('content', follow ? 'noindex, follow' : 'noindex, nofollow');
    robotsMeta.setAttribute('data-injected-by', 'useNoIndex');

    return () => {
      const current = document.querySelector('meta[name="robots"][data-injected-by="useNoIndex"]');
      if (!current) return;
      if (createdByUs) {
        // Fully remove — restore clean default (no robots tag === index, follow)
        current.parentNode && current.parentNode.removeChild(current);
      } else if (previousContent) {
        current.setAttribute('content', previousContent);
        current.removeAttribute('data-injected-by');
      } else {
        current.parentNode && current.parentNode.removeChild(current);
      }
    };
  }, []);
};

/**
 * Defensive SEO safety net for PUBLIC pages.
 *
 * Strips any stray <meta name="robots"> tag on every public route change so a
 * residual `noindex` directive (left behind by a crashed cleanup, a stale
 * prerender snapshot, or a transient catch-all NotFoundPage mount) can never
 * leak onto an indexable public URL.
 *
 * Mount this once at the top of every PUBLIC layout (PublicLayout,
 * StudyAbroadRouter). It is a no-op when the DOM is already clean.
 */
export const useRobotsReset = () => {
  const location = useLocation();
  useEffect(() => {
    // Strip ONLY stray <meta name="robots"> tags (those without an explicit
    // `data-injected-by` marker). Intentional noindex injections from child
    // components (e.g. NotFoundPage, useNoIndex on admin routes) carry the
    // marker and are preserved so they remain effective.
    const stray = document.querySelectorAll('meta[name="robots"]:not([data-injected-by])');
    stray.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    // Same rule for the prerender-status-code hint.
    const prerenderStray = document.querySelectorAll('meta[name="prerender-status-code"]:not([data-injected-by])');
    prerenderStray.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
  }, [location.pathname]);
};

/**
 * Update page title and meta description.
 *
 * - If `title` already contains " | " or "Zafco", it is used as-is (caller
 *   has full control — no double brand suffix).
 * - Otherwise the brand suffix "| Zafco HR" is appended for trust.
 * - Pass an explicit `appendBrand: false` option to opt out entirely.
 *
 * Usage:
 *   usePageMeta('Canada PR Visa 2026: Express Entry & PNP', 'Apply for...');
 *   usePageMeta('Full Custom Title | Zafco HR', '...');  // no double suffix
 */
const ensureMeta = (selector, attrName, attrValue) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  return el;
};

export const usePageMeta = (title, description, options = {}) => {
  const { appendBrand = true, canonical, image, imageAlt, imageWidth = 1200, imageHeight = 630 } = options;
  useEffect(() => {
    if (title) {
      const alreadyBranded =
        title.includes(' | ') ||
        title.toLowerCase().includes('zafco');
      const finalTitle = (!appendBrand || alreadyBranded) ? title : `${title} | Zafco HR`;
      document.title = finalTitle;
      // Mirror to og:title so social-share + Google secondary signals match
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', finalTitle);
    }

    if (description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', description);
      // Mirror to og:description for social-share parity
      let ogDesc = document.querySelector('meta[property="og:description"]');
      if (!ogDesc) {
        ogDesc = document.createElement('meta');
        ogDesc.setAttribute('property', 'og:description');
        document.head.appendChild(ogDesc);
      }
      ogDesc.setAttribute('content', description);
    }

    // Auto-set self-referencing canonical URL.
    // Caller can override by passing options.canonical = '/some/path'
    // or pass `false` to opt out entirely.
    // Defensive: if caller passes an absolute URL (starts with "http"),
    // use it AS-IS — do NOT prefix the origin again (was causing doubled
    // `https://zafco.inhttps://zafco.in/...` canonicals).
    if (canonical !== false) {
      const origin = 'https://zafco.in';
      let canonicalUrl;
      if (typeof canonical === 'string') {
        canonicalUrl = canonical.startsWith('http') ? canonical : `${origin}${canonical}`;
      } else {
        canonicalUrl = `${origin}${window.location.pathname}`;
      }
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    // Sync og:image, og:image:width/height, twitter:card and twitter:image
    // so social-share previews use the page-specific banner when provided.
    if (image) {
      const absoluteImage = image.startsWith('http')
        ? image
        : `https://zafco.in${image.startsWith('/') ? '' : '/'}${image}`;

      const ogImage = ensureMeta('meta[property="og:image"]', 'property', 'og:image');
      ogImage.setAttribute('content', absoluteImage);
      const ogImageW = ensureMeta('meta[property="og:image:width"]', 'property', 'og:image:width');
      ogImageW.setAttribute('content', String(imageWidth));
      const ogImageH = ensureMeta('meta[property="og:image:height"]', 'property', 'og:image:height');
      ogImageH.setAttribute('content', String(imageHeight));
      if (imageAlt) {
        const ogImageAlt = ensureMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt');
        ogImageAlt.setAttribute('content', imageAlt);
      }

      const twCard = ensureMeta('meta[name="twitter:card"]', 'name', 'twitter:card');
      twCard.setAttribute('content', 'summary_large_image');
      const twImage = ensureMeta('meta[name="twitter:image"]', 'name', 'twitter:image');
      twImage.setAttribute('content', absoluteImage);
      if (title) {
        const twTitle = ensureMeta('meta[name="twitter:title"]', 'name', 'twitter:title');
        twTitle.setAttribute('content', document.title);
      }
      if (description) {
        const twDesc = ensureMeta('meta[name="twitter:description"]', 'name', 'twitter:description');
        twDesc.setAttribute('content', description);
      }
    }
  }, [title, description, appendBrand, canonical, image, imageAlt, imageWidth, imageHeight]);
};

/**
 * Inject Schema.org Service JSON-LD for a service / visa / country page.
 * Cleans up on unmount so SPA navigation does not leak stale schemas.
 *
 * Usage:
 *   useServiceSchema({
 *     name: 'Canada PR Visa Consulting',
 *     description: 'Express Entry & PNP application support…',
 *     serviceType: 'Immigration Consulting',
 *     areaServed: 'Canada',
 *     priceRange: '$$',
 *   });
 */
export const useServiceSchema = (service) => {
  useEffect(() => {
    if (!service || !service.name) return undefined;
    const tagId = `service-schema-${service.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 60)}`;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: service.name,
      description: service.description,
      serviceType: service.serviceType || 'Overseas Recruitment',
      provider: { '@id': 'https://zafco.in/#organization' },
      areaServed: Array.isArray(service.areaServed)
        ? service.areaServed.map((a) => ({ '@type': 'Country', name: a }))
        : { '@type': 'Country', name: service.areaServed || 'India' },
      audience: { '@type': 'Audience', audienceType: service.audience || 'Indian professionals seeking overseas opportunities' },
      ...(service.offers && { offers: service.offers }),
      ...(service.url && { url: service.url }),
    };

    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-service-schema', tagId);
    el.text = JSON.stringify(schema)
      .replace(/<\/(script)/gi, '<\\/$1')
      .replace(/<!--/g, '<\\!--');
    document.head.appendChild(el);
    return () => {
      document.head.querySelector(`script[data-service-schema="${tagId}"]`)?.remove();
    };
  }, [service]);
};

/**
 * Inject Schema.org FAQPage JSON-LD from a list of {q, a} objects.
 *
 * SINGLE GLOBAL AUTHORITY (Feb 2026 fix): On every render this hook first
 * REMOVES every existing FAQPage JSON-LD script anywhere in the document —
 * whether injected by this hook, by an inline `<script>` in a legacy page,
 * or left over from a previous SPA route that didn't unmount cleanly. Only
 * after the sweep does it inject exactly ONE new FAQPage script. On cleanup
 * (unmount / route change) it sweeps again with `querySelectorAll`.
 *
 * This eliminates the "Duplicate field 'FAQPage'" Rich Results validation
 * error that was hitting 30+ sitewide URLs (Feb 2026 GSC report).
 *
 * Usage:
 *   useFAQSchema([{ q: '...', a: '...' }, ...]);
 */
const FAQ_SCHEMA_MARKER = 'data-zafco-faq-schema';

const removeAllFAQSchemaScripts = () => {
  // Remove by marker (canonical scripts)
  document.head
    .querySelectorAll(`script[${FAQ_SCHEMA_MARKER}]`)
    .forEach((el) => el.remove());
  // Defensive content-sweep: also remove ANY ld+json script whose text declares
  // @type=FAQPage. Catches legacy pages that inline-inject with custom markers.
  document.head
    .querySelectorAll('script[type="application/ld+json"]')
    .forEach((el) => {
      const text = el.textContent || '';
      if (text.includes('"@type":"FAQPage"') || text.includes('"@type": "FAQPage"')) {
        el.remove();
      }
    });
};

export const useFAQSchema = (faqs) => {
  useEffect(() => {
    if (!Array.isArray(faqs) || faqs.length === 0) return undefined;
    // Sweep ANY pre-existing FAQPage scripts (from this hook, inline injectors,
    // or stale route navigation) before adding the canonical one.
    removeAllFAQSchemaScripts();
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute(FAQ_SCHEMA_MARKER, '1');
    el.text = JSON.stringify(schema)
      .replace(/<\/(script)/gi, '<\\/$1')
      .replace(/<!--/g, '<\\!--');
    document.head.appendChild(el);
    return () => {
      // On unmount / route change, sweep ALL FAQPage scripts. Prevents stale
      // schemas leaking onto the next route.
      removeAllFAQSchemaScripts();
    };
  }, [faqs]);
};

/**
 * Defensive safety-net hook — mount once at the top of every layout
 * (PublicLayout, StudyAbroadLayout, AdminLayout). On every route change it
 * sweeps for duplicate FAQPage JSON-LD scripts and keeps only the first one.
 *
 * Why this exists: ~20 legacy pages inline-inject FAQPage scripts with
 * inconsistent `data-*-faq-schema` markers. If a user navigates from page A
 * to page B and A's custom cleanup fails (Suspense unmount race, prerender
 * snapshot quirk, etc.), B's schema can co-exist with A's stale one, causing
 * "Duplicate field 'FAQPage'" Rich Results errors. This hook is the belt to
 * `useFAQSchema`'s braces.
 */
export const useFAQSchemaDedupe = () => {
  const location = useLocation();
  useEffect(() => {
    const scripts = document.head.querySelectorAll('script[type="application/ld+json"]');
    let seenFAQ = false;
    scripts.forEach((el) => {
      const text = el.textContent || '';
      if (!text.includes('FAQPage')) return;
      if (!seenFAQ) {
        seenFAQ = true; // keep the first one
      } else {
        el.remove(); // remove all subsequent duplicates
      }
    });
  }, [location.pathname]);
};

export default useCanonicalURL;
